import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ChartBarIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  ArrowTrendingUpIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { salesAPI, inventoryAPI, aiAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    sales: { total_sales_count: 0, total_revenue: 0, unique_products: 0 },
    inventory: { total_items: 0, total_stock_value: 0, low_stock_items: 0 },
    insights: []
  });
  const [recentInsights, setRecentInsights] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const results = await Promise.allSettled([
        salesAPI.getSummary(),
        inventoryAPI.getSummary(),
        aiAPI.getQuickInsights()
      ]);

      const salesStats = results[0].status === 'fulfilled' ? results[0].value.data : { total_sales_count: 0, total_revenue: 0, unique_products: 0 };
      const inventoryStats = results[1].status === 'fulfilled' ? results[1].value.data : { total_items: 0, total_stock_value: 0, low_stock_items: 0 };
      const aiInsights = results[2].status === 'fulfilled' ? results[2].value.data : { insights: [] };

      setStats({
        sales: salesStats,
        inventory: inventoryStats,
        insights: []
      });

      // Ensure insights is always an array of valid objects
      const insightsArray = Array.isArray(aiInsights?.insights)
        ? aiInsights.insights.filter(insight => insight && typeof insight === 'object' && insight.message)
        : [];

      setRecentInsights(insightsArray);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
      // Set empty data on error
      setStats({
        sales: { total_sales_count: 0, total_revenue: 0, unique_products: 0 },
        inventory: { total_items: 0, total_stock_value: 0, low_stock_items: 0 },
        insights: []
      });
      setRecentInsights([]);
    } finally {
      setLoading(false);
    }
  };

  const triggerAIAnalysis = async () => {
    try {
      toast.loading('Running AI analysis...', { id: 'ai-analysis' });

      await aiAPI.analyze({ analysis_period_days: 30 });

      toast.success('AI analysis completed!', { id: 'ai-analysis' });

      // Refresh insights
      loadDashboardData();
    } catch (error) {
      toast.error('AI analysis failed', { id: 'ai-analysis' });
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle, link }) => (
    <div className="relative group rounded-2xl p-[1px] bg-gradient-to-b from-gray-200 to-transparent hover:from-amber-400 hover:to-amber-600 transition-all duration-500 shadow-sm hover:shadow-xl">
      <div className="bg-white rounded-[15px] h-full p-6 flex flex-col justify-between relative overflow-hidden">
        {/* Decorative background blob */}
        <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 blur-2xl ${color}`}></div>

        <div className="flex items-center">
          <div className={`p-4 rounded-xl ${color} bg-opacity-10 backdrop-blur-md border border-white/20 shadow-sm relative z-10`}>
            <Icon className={`h-6 w-6 text-${color.includes('bg-') ? color.split('-')[1] : 'blue'}-600`} />
          </div>
          <div className="ml-5 flex-1 relative z-10">
            <p className="text-sm font-bold tracking-wider text-gray-500 uppercase">{title}</p>
            <p className="text-3xl font-black text-gray-900 mt-1 font-serif tracking-tight">{value}</p>
            {subtitle && (
              <p className="text-sm font-medium text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
        </div>
        {link && (
          <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between relative z-10">
            <Link
              to={link}
              className={`text-${color.includes('bg-') ? color.split('-')[1] : 'blue'}-600 hover:text-${color.includes('bg-') ? color.split('-')[1] : 'blue'}-800 text-sm font-bold flex items-center transition-colors duration-200 ml-auto uppercase tracking-widest`}
            >
              View details
              <svg className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </div>
  );

  const QuickActionCard = ({ title, description, icon: Icon, color, onClick, link }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 hover:shadow-xl transition-all duration-300 group cursor-pointer relative overflow-hidden" onClick={onClick ? onClick : () => { }}>
      <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
      <div className={`inline-flex p-4 rounded-xl ${color} bg-opacity-10 mb-6 border border-gray-100 shadow-inner group-hover:scale-110 transition-transform duration-300`}>
        <Icon className={`h-8 w-8 text-${color.includes('bg-') ? color.split('-')[1] : 'blue'}-600`} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3 font-serif">{title}</h3>
      <p className="text-gray-500 text-sm mb-6 leading-relaxed relative z-10">{description}</p>
      {onClick ? (
        <button
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          className={`inline-flex items-center px-6 py-3 border-2 border-transparent text-sm font-bold rounded-lg text-white ${color} hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors uppercase tracking-widest shadow-md`}
        >
          Execute Action
        </button>
      ) : (
        <Link
          to={link}
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center px-6 py-3 border-2 border-transparent text-sm font-bold rounded-lg text-white ${color} hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors uppercase tracking-widest shadow-md`}
        >
          Get Started
        </Link>
      )}
    </div>
  );

  const InsightCard = ({ insight }) => {
    // Safety check to prevent rendering objects
    if (!insight || typeof insight !== 'object' || !insight.message || typeof insight.message !== 'string') {
      return null;
    }

    const getInsightColor = (type) => {
      switch (type) {
        case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
        case 'error': return 'bg-red-50 border-red-200 text-red-800';
        case 'success': return 'bg-green-50 border-green-200 text-green-800';
        case 'info': return 'bg-blue-50 border-blue-200 text-blue-800';
        default: return 'bg-gray-50 border-gray-200 text-gray-800';
      }
    };

    const getIcon = (type) => {
      switch (type) {
        case 'warning': return ExclamationTriangleIcon;
        case 'error': return ExclamationTriangleIcon;
        case 'success': return ArrowTrendingUpIcon;
        default: return BeakerIcon;
      }
    };

    const Icon = getIcon(insight.type);

    return (
      <div className={`rounded-lg border p-4 ${getInsightColor(insight.type)}`}>
        <div className="flex items-start">
          <Icon className="h-5 w-5 mt-0.5 mr-3" />
          <div>
            <h4 className="font-medium text-sm">{insight.title || 'Insight'}</h4>
            <p className="text-sm mt-1 opacity-90">{insight.message}</p>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gray-900 rounded-3xl p-8 shadow-2xl border border-gray-800">
        {/* Background texture for premium look */}
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        <div className="absolute -right-20 -top-20 w-64 h-64 bg-amber-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between">
          <div>
            <h1 className="text-4xl font-serif font-bold text-white tracking-tight">Innventory<span className="text-amber-400">AI</span> Dashboard</h1>
            <p className="text-gray-400 mt-2 text-lg font-light">Intelligent insights for your hospitality operations.</p>
          </div>
          <div className="mt-6 md:mt-0 flex gap-4">
            <button className="px-6 py-2.5 bg-white/10 text-white font-bold rounded-lg border border-white/20 hover:bg-white/20 backdrop-blur-md transition-all uppercase tracking-widest text-sm" onClick={loadDashboardData}>
              Refresh Data
            </button>
            <button onClick={triggerAIAnalysis} className="px-6 py-2.5 bg-amber-500 text-gray-900 font-bold rounded-lg hover:bg-amber-400 shadow-lg shadow-amber-500/30 transition-all uppercase tracking-widest text-sm flex items-center">
              <BeakerIcon className="w-5 h-5 mr-2" />
              Run Analysis
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Sales"
          value={stats.sales?.total_sales_count || 0}
          subtitle={`$${(stats.sales?.total_revenue || 0).toFixed(2)} revenue`}
          icon={ChartBarIcon}
          color="bg-blue-500"
          link="/app/sales"
        />
        <StatCard
          title="Inventory Items"
          value={stats.inventory?.total_items || 0}
          subtitle={`$${(stats.inventory?.total_stock_value || 0).toFixed(2)} total value`}
          icon={CubeIcon}
          color="bg-green-500"
          link="/app/inventory"
        />
        <StatCard
          title="Low Stock Alerts"
          value={stats.inventory?.low_stock_items || 0}
          subtitle="Items need reordering"
          icon={ExclamationTriangleIcon}
          color={(stats.inventory?.low_stock_items || 0) > 0 ? "bg-red-500" : "bg-gray-500"}
          link="/app/inventory"
        />
        <StatCard
          title="Products Sold"
          value={stats.sales?.unique_products || 0}
          subtitle="Unique products"
          icon={ArrowTrendingUpIcon}
          color="bg-purple-500"
          link="/app/sales"
        />
      </div>

      {/* Performance Overview */}
      <div className="bg-gray-900 rounded-3xl p-8 shadow-2xl relative overflow-hidden border border-gray-800">
        <div className="absolute top-0 right-0 w-full h-full opacity-10" style={{ backgroundImage: 'radial-gradient(circle at right top, #fbbf24, transparent 50%)' }}></div>
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-white mb-6 font-serif">Performance Overview</h2>
          <div className="h-64 flex items-center justify-center border border-gray-700/50 rounded-2xl bg-gray-800/50 backdrop-blur-sm shadow-inner group transition-all duration-300 hover:bg-gray-800/80">
            <div className="text-center transform group-hover:scale-105 transition-transform duration-300">
              <ChartBarIcon className="h-16 w-16 text-amber-500 mx-auto mb-4 opacity-80" />
              <p className="text-amber-400 font-bold text-lg uppercase tracking-widest">Analytics Dashboard</p>
              <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto leading-relaxed">Gathering data to generate your personalized real-time performance charting.</p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      {recentInsights.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Insights</h2>
          <div className="space-y-3">
            {recentInsights.map((insight, index) => (
              <InsightCard key={index} insight={insight} />
            ))}
          </div>
          <div className="mt-4">
            <Link
              to="/app/ai-analysis"
              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              View detailed AI analysis
              <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Getting Started</h2>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-blue-600">1</span>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 border-b pb-1 border-gray-100">Set up your inventory</h3>
                <p className="text-sm text-gray-600 mt-2">Add your hotel's products and current stock levels</p>
                <Link to="/app/inventory" className="text-sm text-blue-600 hover:text-blue-700 mt-1 inline-flex items-center">
                  Go to Inventory <svg className="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-blue-600">2</span>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Record sales transactions</h3>
                <p className="text-sm text-gray-600">Track your sales to enable AI analysis</p>
                <Link to="/app/sales" className="text-sm text-blue-600 hover:text-blue-700 mt-1 inline-flex items-center">
                  Go to Sales <svg className="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-blue-600">3</span>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Get AI recommendations</h3>
                <p className="text-sm text-gray-600">Let AI analyze your data and provide reorder suggestions</p>
                <Link to="/app/ai-analysis" className="text-sm text-blue-600 hover:text-blue-700 mt-1 inline-flex items-center">
                  Run Analysis <svg className="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;